<template>
  <div
    :id="'donut-chart-container-' + tag.name + segmentName"
    class="donut-chart-container"
  >
    <div :id="'chart-' + tag.name + segmentName"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'

const addArrow = (svg, pie, shouldHideCenterLabel = false) => {
  const markerBoxWidth = 5
  const markerBoxHeight = 5
  const refX = 2.5
  const refY = 2.5
  const arrowPoints = [
    [0, 0],
    [0, 5],
    [5, 2.5]
  ]
  const arrowWidth = 20
  const arrowYOffset = -45
  const arrowContainer = pie.append('g').attr('id', 'arrow-container')

  svg
    .append('defs')
    .append('marker')
    .attr('id', 'arrow-head')
    .attr('viewBox', [0, 0, markerBoxWidth, markerBoxHeight])
    .attr('refX', refX)
    .attr('refY', refY)
    .attr('markerWidth', markerBoxWidth)
    .attr('markerHeight', markerBoxHeight)
    .attr('orient', 'auto-start-reverse')
    .append('path')
    .attr('d', d3.line()(arrowPoints))
    .style('opacity', 1)

  arrowContainer
    .append('rect')
    .attr('width', arrowWidth)
    .attr('height', arrowWidth / 2)
    .attr('x', -arrowWidth / 2)
    .attr('y', arrowYOffset - 5)
    .attr('fill', 'none')
    .attr('stroke', 'black')
    .attr('stroke-width', 10)
    .style('opacity', 0)

  arrowContainer
    .append('line')
    .attr('id', 'arrow')
    .attr('x1', -arrowWidth / 2)
    .attr('y1', arrowYOffset)
    .attr('x2', arrowWidth / 2)
    .attr('y2', arrowYOffset)
    .attr('stroke', '#666')
    .attr('stroke-width', 2)
    .attr('marker-end', 'url(#arrow-head)')
    .style('opacity', 1)

  return arrowContainer
}

const addCenterValue = ({
  pie,
  cursor,
  shouldHideCenterLabel = false,
  hasNoData = false
}) => {
  const y = shouldHideCenterLabel ? 27 : 10
  const fontSize = hasNoData ? '2.5em' : '3.0em'
  const fontColor = hasNoData
    ? 'var(--kt-text-muted)'
    : 'var(--kt-headings-color)'
  return (
    pie
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('font-size', fontSize)
      .style('cursor', cursor)
      .style('fill', fontColor)
      // .style('cursor', 'default')
      .attr('y', y) // if shouldHideCenterLabel is true, then it should be centered
  )
}

// not called if shouldHideCenterLabel is true
const addCenterLabel = ({ pie, label, hasNoData = false }) => {
  const fontSize = label.length > 15 ? '1.0em' : '1.2em'
  const fontColor = hasNoData
    ? 'var(--kt-text-muted)'
    : 'var(--kt-headings-color)'
  if (label.length > 15 && label.includes(' ')) {
    // a long label that has sapces
    const words = label.split(' ')
    const mid = Math.round(words.length / 2)
    const l1 = words.slice(0, mid).join(' ')
    const l2 = words.slice(mid, words.length).join(' ')
    const textGroup = pie.append('g')
    textGroup
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('font-size', fontSize)
      .attr('y', 35)
      .style('cursor', 'default')
      .style('fill', fontColor)
      .text(l1)
    textGroup
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('font-size', fontSize)
      .attr('y', 55)
      .style('cursor', 'default')
      .style('fill', fontColor)
      .text(l2)
    return textGroup
  } else {
    return pie
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('font-size', fontSize)
      .attr('y', 40)
      .style('cursor', 'default')
      .style('fill', fontColor)
      .text(label)
  }
}

const addTooltip = (donutName, segmentName = '') => {
  return d3
    .select(`#donut-chart-container-${donutName}${segmentName}`)
    .append('div')
    .style('opacity', 0)
    .style('box-shadow', '0px 5px 25px 0px rgba(0, 0, 0, 0.2')
    .style('cursor', 'default')
    .attr('class', 'pie-tooltip')
    .style('pointer-events', 'none')
    .style('z-index', 500)
}

export default {
  name: 'DonutChart',
  props: {
    theme: String,
    // TODO: maybe rename tag to statItem since it's being used by CategoryDetailsOverview when showResultsPerPerson
    tag: Object,
    shouldHideCenterLabel: Boolean,
    userIsAdminForCurrentGroupOrSuperAdmin: Boolean,
    donutData: Object, // { tagTitle, tagDescription, chartData, value, trend: {status: same/increased/decreased/null, tooltip}  }
    onClickCallback: Function,
    segName: String,
    resultPerUser: String
  },
  data() {
    return {
      centerLabelText: this.donutData?.tagTitle || ''
    }
  },
  computed: {
    hasNoData() {
      return this.donutData.currentValue === null
    },
    segmentName() {
      return this.segName ? this.segName.replace(/[^\w\s]/g, '') : ''
    }
  },
  watch: {
    donutData(newChartData) {
      this.drawChart(this.donutData.chartData)
    },
    hasNoData(newHasNoData, oldHasNoData) {
      // new graph has no data, old graph used to have data
      if (newHasNoData && !oldHasNoData) {
        if (this.trendArrow) {
          this.trendArrow.style('opacity', 0)
        }
        // new graph has data, old graph used to have no data
      } else if (!newHasNoData && oldHasNoData) {
        if (!this.trendArrow) {
          var svg = d3.select(`#chart-${this.tag.name}${this.segmentName}`)
          this.trendArrow = addArrow(
            svg,
            this.pieG,
            this.shouldHideCenterLabel,
            this.hasNoData
          )
        } else if (this.trendArrow) {
          this.trendArrow.style('opacity', 1)
        }
      }
    }
  },
  mounted() {
    this.prepareChart(this.segmentName)
  },
  methods: {
    didClickValue: function () {
      if (this.userIsAdminForCurrentGroupOrSuperAdmin) {
        const callbackData = {
          name: 'tag-details',
          data: this.tag,
          size: 'lg',
          theme: this.theme
        }
        if (this.onClickCallback) {
          this.onClickCallback(callbackData)
        }
      }
    },
    prepareChart: function () {
      var viewboxWidth = 200
      var viewboxHeight = 200
      var svg = d3
        .select(`#chart-${this.tag.name}${this.segmentName}`)
        .append('svg')
        .attr('viewBox', `0 0 ${viewboxWidth} ${viewboxHeight}`)

      var margin = { top: 0, left: 0, bottom: 0, right: 0 }
      var chartWidth = viewboxWidth - (margin.left + margin.right)
      var chartHeight = viewboxHeight - (margin.top + margin.bottom)
      this.chartLayer = svg
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`)

      this.arc = d3
        .arc()
        .outerRadius(chartHeight / 2)
        .innerRadius(chartHeight / 2.5)
        .padAngle(0.03)
        .cornerRadius(3)

      this.pieG = this.chartLayer
        .append('g')
        .attr('transform', `translate(${chartWidth / 2}, ${chartHeight / 2})`)
      if (!this.shouldHideCenterLabel && !this.hasNoData) {
        this.trendArrow = addArrow(
          svg,
          this.pieG,
          this.shouldHideCenterLabel,
          this.hasNoData
        )
      }

      // dont do below if shouldHideCenterValue is true

      this.centerValue = addCenterValue({
        pie: this.pieG,
        cursor:
          this.userIsAdminForCurrentGroupOrSuperAdmin &&
          !this.shouldHideCenterLabel &&
          !this.hasNoData &&
          this.onClickCallback
            ? 'pointer'
            : 'cursor',
        // adds the 'pointer', dont do if shouldHideCenterValue is true
        shouldHideCenterLabel: this.shouldHideCenterLabel,
        hasNoData: this.hasNoData
      })
      if (!this.shouldHideCenterLabel) {
        if (!this.hasNoData) {
          this.centerValue.on('click', this.didClickValue)
        }

        if (this.onClickCallback && !this.hasNoData) {
          this.centerValue.on('mouseenter', function () {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('font-size', '4.0em')
          })
          this.centerValue.on('mouseleave', function () {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('font-size', '3.3em')
          })
        }
        this.centerLabel = addCenterLabel({
          pie: this.pieG,
          label: this.donutData.tagTitle,
          hasNoData: this.hasNoData
        })
        this.centerLabelText = this.donutData.tagTitle
      }
      if (this.tag?.name) {
        this.tooltip = addTooltip(this.tag.name, this.segmentName)
      }
      this.drawChart(this.donutData.chartData)
    },
    showTooltip: function (d, coords, html) {
      if (this.tooltip) {
        const tooltipOffset = 50
        this.tooltip.transition().duration(200).style('opacity', 1)
        this.tooltip
          .html(html)
          .style('left', coords[0] + tooltipOffset + 'px')
          .style('top', coords[1] + tooltipOffset + 'px')
      }
    },
    moveTooltip: function (d, coords) {
      if (this.tooltip) {
        const marginTop = 10
        const tooltipBox = this.tooltip.node().getBoundingClientRect()
        const tooltipOffsetX = -tooltipBox.width / 2 // half tooltip
        const tooltipOffsetY = -tooltipBox.height - marginTop

        // potential for solution if we would clip right/bottom
        // const swiper = d3.select(`.swiper`).node()
        // const swiperRect = swiper.getBoundingClientRect()
        // const containerWidth = swiperRect.width
        // const containerHeight = swiperRect.height

        let offsetX = tooltipOffsetX
        let offsetY = tooltipOffsetY

        // too close to the left edge? then move it to the right
        if (event.layerX - tooltipBox.width / 2 < 0) {
          offsetX = offsetX + tooltipBox.width / 4
        }

        // to close to the top edge? then move it down
        if (event.layerY - tooltipBox.height < 0) {
          offsetY = offsetY + tooltipBox.height / 5
        }

        this.tooltip
          .style('left', event.layerX + offsetX + 'px')
          .style('top', event.layerY + offsetY + 'px')
      }
    },
    hideTooltip: function (d) {
      if (this.tooltip) {
        this.tooltip.transition().duration(200).style('opacity', 0)
      }
    },
    drawChart: function (data) {
      const that = this
      if (data) {
        var arcs = d3
          .pie()
          .sort(null)
          .value(function (d) {
            return d.value
          })(data)

        var block = this.pieG.selectAll('.arc').data(arcs)

        block.select('path').attr('d', this.arc)
        var newBlock = block
          .enter()
          .append('g')
          .classed('arc', true)
          .on('mouseenter', function (d, target) {
            if (target.data.tooltip) {
              that.showTooltip(d, d3.pointer(this), target.data.tooltip)
            }
          })
          .on('mousemove', function (d) {
            that.moveTooltip(d, d3.pointer(this))
          })
          .on('mouseleave', function (d) {
            that.hideTooltip(d)
          })

        newBlock
          .append('path')
          .attr('d', this.arc)
          .attr('class', function (d, i) {
            return 'arc-slice-' + i
          })
          .attr('id', function (d, i) {
            return 'arc-slice'
          })
        // .attr('fill', d => 'white')

        this.pieG
          .selectAll('#arc-slice')
          // .attr('fill', '#eee')
          // // .style('fill', (d) => 'var(--kt-gray-300)')
          .attr('fill', (d) => d.data.color)
          .transition()
          .duration(800)
          .delay((d, i) => 400 * i)
          .ease(d3.easeCubic)

        if (!this.shouldHideCenterLabel && this.centerLabel) {
          const newLabelText = this.donutData.tagTitle
          if (newLabelText !== this.centerLabelText) {
            this.centerLabel.remove()
            this.centerLabel = addCenterLabel({
              pie: this.pieG,
              label: newLabelText,
              hasNoData: this.hasNoData
            })
            this.centerLabelText = newLabelText
          }
        }

        this.centerValue
          .text(this.donutData.currentValueTitle)
          // .style('fill', fontColor)
          .transition()
          .duration(800)
          .attr('font-size', this.hasNoData ? '2.7em' : '4.0em')
          .transition()
          .duration(800)
          .ease(d3.easeCubic)
          .attr('font-size', this.hasNoData ? '2.5em' : '3.3em')

        if (!this.shouldHideCenterLabel && !this.hasNoData) {
          this.centerLabel
            .on('mouseover', function (d, target) {
              const html = that.donutData.tagDescription
              that.showTooltip(d, d3.pointer(this), html)
            })
            .on('mousemove', function (d) {
              that.moveTooltip(d, d3.pointer(this))
            })
            .on('mouseleave', function (d) {
              that.hideTooltip(d)
            })

          if (this.trendArrow) {
            if (this.donutData.trend.hasTrend) {
              this.trendArrow
                .on('mouseenter', function (d) {
                  const html = that.donutData.trend.tooltip // trendText
                  that.showTooltip(d, d3.pointer(this), html)
                })
                .on('mousemove', function (d) {
                  that.moveTooltip(d, d3.pointer(this))
                })
                .on('mouseleave', function (d) {
                  that.hideTooltip(d)
                })

              if (this.donutData.trend.direction === 'increased') {
                this.trendArrow
                  .style('opacity', 1)
                  .attr('transform', 'translate(30, -15) rotate(-45)') // up
              } else if (this.donutData.trend.direction === 'decreased') {
                this.trendArrow
                  .style('opacity', 1)
                  .attr('transform', 'translate(-30, -15) rotate(45)') // down
              } else {
                this.trendArrow
                  .style('opacity', 1)
                  .attr('transform', 'translate(0, 0) rotate(0)') // right
              }
              // .style('fill', this.colorForSummary(this.currentAverage.npsData.summary))
            } else {
              this.trendArrow.style('opacity', 0)
            }
          }
        }
      }
    }
  }
}
</script>

<style lang="scss">
.donut-chart-container {
  margin-left: 0.8rem;
  margin-right: 0.8rem;
}

.pie-tooltip {
  position: absolute;
  background-color: var(--kt-input-bg);
  max-width: 260px;
  border-radius: 5px;
  padding: 10px;
  z-index: 10;
}

.pie-center-label {
  background: red;
}

marker#arrow-head {
  fill: #666;
}

marker#arrow {
  fill: #666;
}

.trend-positive {
  transform: rotate(-45deg);
}

.trend-negative {
  transform: rotate(45deg);
}
</style>
